import { useRef, useState } from 'react';

import { cloneDeep as _cloneDeep, get as _get, merge as _merge, set as _set } from 'lodash';

import { FormHelpers } from '@sine/sine-ui-components';

const DEFAULT_DEBOUNCE_WAIT = 200;

function flattenNestedErrors(nestedErrors) {
  if (!(nestedErrors instanceof Array)) {
    const flattenedNestedErrors = [];

    for (const error in nestedErrors) {
      if (Object.hasOwnProperty.call(nestedErrors, error)) {
        flattenedNestedErrors.push(...flattenNestedErrors(nestedErrors[error]));
      }
    }

    return flattenedNestedErrors;
  }

  return nestedErrors;
}

function flattenErrors(errors) {
  for (const error in errors) {
    if (Object.hasOwnProperty.call(errors, error)) {
      errors[error] = flattenNestedErrors(errors[error]);
    }
  }
  return errors;
}

function validateForm(schema, values, fieldsToInclude, validationOptions = {}) {
  const result = schema.validate(values, { abortEarly: false, ...validationOptions });

  if (!result.error) {
    return {
      errors: {},
      hasError: false,
    };
  }

  const filteredJoiResult = !fieldsToInclude ? result : FormHelpers.filterJoiError(result, { fieldsToInclude });
  const errors = flattenErrors(FormHelpers.joiResultToErrorObject(filteredJoiResult));
  const hasError = FormHelpers.containsError(errors);

  return {
    errors,
    hasError,
  };
}

/** @deprecated Use UIC `useJoiForm` instead. */
export default function useJoiForm(schema, initialValues, validationOptions = {}) {
  const [values, setValues] = useState(initialValues || {});
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  const _validate = (formValues, fieldsToInclude) =>
    validateForm(schema, formValues, fieldsToInclude, validationOptions);

  /**
   * Sets the form field's error.
   * @param {string} name The name of the form field.
   * @param {any} error The associated error.
   */
  const onError = (name, error) => {
    setErrors((prevErrors) => _set({ ...prevErrors }, name, error));
  };

  /**
   * The form field change event.
   * @param {string} name The name of the form field.
   * @param {any} value The updated form field value.
   * @param {boolean} validate Whether to run validation on the change. Default: `true`.
   */
  const onChange = (name, value, validate = true) => {
    // For anyone reading this code later:
    // It's generally ill-advised to put a setState inside another setState, unless you know
    // exactly what the data flow looks like. Only adopt this pattern if there is no other choice.
    setValues((prevValues) => {
      const newValues = _set({ ...prevValues }, name, value);

      if (validate) {
        const result = _validate(newValues, _set({}, name, validate));
        onError(name, _get(result.errors, name));
        setIsValid(!result.hasError);
      }

      return newValues;
    });
  };

  /**
   * Updates and overrides all form values.
   * @param {any} value The updated form values.
   * @param {boolean} validate Whether to run validation on the change. Default: `true`.
   */
  const onChangeAll = (value, validate = true) => {
    const newValues = _merge(_cloneDeep(initialValues), value);

    setValues(newValues);

    if (validate) {
      const result = validateForm(schema, newValues);

      setErrors((prevErrors) => ({
        ...prevErrors,
        ...result.errors,
      }));
      setIsValid(!result.hasError);
    }
  };

  /**
   * The form field blur event. Runs validation for the field.
   * @param {string} name The name of the form field.
   * @param {boolean} validate Whether to run validation on the change. Default: `true`.
   */
  const onBlur = (name, validate = true) => {
    if (validate) {
      const result = _validate(values, _set({}, name, validate));
      onError(name, _get(result.errors, name));
      setIsValid(!result.hasError);
    }
  };

  /**
   * Validates all the form values.
   * @returns {boolean} Whether valid or not.
   */
  const validate = () => {
    const result = _validate(values);
    setErrors(result.errors);
    setIsValid(!result.hasError);
    return !result.hasError;
  };

  /**
   * Validates the schema and return transformed values.
   * This function triggers `trims`, `defaults` and `converts` on all the compatible schema's values.
   */
  const validateAndTransform = () => {
    const _isValid = validate();
    // calling OG joi.validate which triggers trims and converts.
    const { value: _values } = schema.validate(values);
    // values instead of value just to match actual implementation.
    return { isValid: _isValid, values: _values };
  };

  /**
   * Resets the whole form state.
   */
  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setIsValid(false);
  };

  /**
   * Resets the error states.
   */
  const resetErrors = () => {
    setErrors({});
    setIsValid(false);
  };

  const refValidate = useRef();
  const refOnChange = useRef();
  const refOnBlur = useRef();
  const refOnError = useRef();

  const _debounce = (ref, fn, wait = DEFAULT_DEBOUNCE_WAIT) => {
    ref.current && clearTimeout(ref.current);
    ref.current = setTimeout(fn, wait);
  };

  /**
   * Validates all the form values after a set period of time.
   */
  const debouncedValidate = (wait) => {
    _debounce(refValidate, () => validate(), wait);
  };

  /**
   * The form field change event. Enacted after a set period of time.
   * @param {string} name The name of the form field.
   * @param {any} value The updated form field value.
   * @param {number} wait How long to wait before executing. Default: `DEFAULT_DEBOUNCE_WAIT`.
   */
  const onDebouncedChange = (name, value, wait) => {
    _debounce(refOnChange, () => onChange(name, value), wait);
  };

  /**
   * The form field blur event. Runs validation for the field after a set period of time.
   * @param {string} name The name of the form field.
   * @param {number} wait How long to wait before executing. Default: `DEFAULT_DEBOUNCE_WAIT`.
   */
  const onDebouncedBlur = (name, wait) => {
    _debounce(refOnBlur, () => onBlur(name), wait);
  };

  /**
   * Sets the form field's error after a set period of time.
   * @param {string} name The name of the form field.
   * @param {any} error The associated error.
   * @param {number} wait How long to wait before executing. Default: `DEFAULT_DEBOUNCE_WAIT`.
   */
  const onDebouncedError = (name, error, wait) => {
    _debounce(refOnError, () => onError(name, error), wait);
  };

  return {
    values,
    errors,
    isValid,
    validate,
    validateAndTransform,
    reset,
    resetErrors,
    onChange,
    onBlur,
    onError,
    onChangeAll,
    debouncedValidate,
    onDebouncedChange,
    onDebouncedBlur,
    onDebouncedError,
  };
}

/**
 * Validates all Joi forms in the provided object.
 * @param {Array<object>} forms An object of Joi forms.
 * @returns {boolean} Whether the collection is valid or not.
 */
export function validateObjectOfForms(forms) {
  const valids = [];
  const values = [];

  for (const id in forms) {
    valids.push(forms[id].validate());
    values.push(forms[id].values);
  }
  return {
    isValid: !valids.includes(false),
    values,
  };
}

/**
 * Validates and Transforms all Joi forms in the provided object.
 * @param {Array<object>} forms An object of Joi forms.
 * @returns {boolean} Whether the collection is valid or not.
 */
export function validateAndTransformObjectOfForms(forms) {
  const valids = [];
  const values = [];

  for (const id in forms) {
    valids.push(forms[id].validate());
    values.push(forms[id].validateAndTransform().values);
  }
  return {
    isValid: !valids.includes(false),
    values,
  };
}
